import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Download, BookOpen, Eye } from 'lucide-react';
import SEO from '../components/SEO';

const PublicationsPage = () => {
  const publications = [
    {
      id: 1,
      slug: 'trademark-registration-masterclass-2024',
      title: 'Trademark Registration Masterclass 2024',
      description: 'Complete step-by-step training program covering every aspect of trademark registration from initial concept to final certificate.',
      type: 'eBook',
      pages: 68,
      format: 'PDF',
      size: '5.4 MB',
      featured: true,
      image: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=800&h=600&fit=crop'
    },
    {
      id: 2,
      slug: 'brand-protection-strategies-digital-commerce',
      title: 'Brand Protection Strategies for Digital Commerce',
      description: 'Essential tactics for safeguarding your brand in online marketplaces, e-commerce platforms, and digital channels.',
      type: 'Whitepaper',
      pages: 42,
      format: 'PDF',
      size: '3.8 MB',
      featured: true,
      image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800&h=600&fit=crop'
    },
    {
      id: 3,
      slug: 'uspto-filing-guide-complete-walkthrough',
      title: 'USPTO Filing Guide: Complete Walkthrough',
      description: 'Comprehensive instructions for navigating the USPTO filing system with expert tips to avoid common mistakes and delays.',
      type: 'Guide',
      pages: 55,
      format: 'PDF',
      size: '4.7 MB',
      featured: false,
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=600&fit=crop'
    },
    {
      id: 4,
      slug: 'startup-trademark-essentials-handbook',
      title: 'Startup Trademark Essentials Handbook',
      description: 'Everything new entrepreneurs need to know about protecting their brand from day one, with budget-friendly strategies.',
      type: 'eBook',
      pages: 48,
      format: 'PDF',
      size: '4.1 MB',
      featured: false,
      image: 'https://images.unsplash.com/photo-1590097520283-52d3d7926a86?w=800&h=600&fit=crop'
    },
    {
      id: 5,
      slug: 'international-brand-protection-framework',
      title: 'International Brand Protection Framework',
      description: 'Navigate global trademark registration with this comprehensive guide to the Madrid Protocol and international filing systems.',
      type: 'eBook',
      pages: 72,
      format: 'PDF',
      size: '6.2 MB',
      featured: false,
      image: 'https://images.unsplash.com/photo-1462899006636-339e08d1844e?w=800&h=600&fit=crop'
    },
    {
      id: 6,
      slug: 'trademark-enforcement-playbook',
      title: 'Trademark Enforcement Playbook',
      description: 'Proven strategies for monitoring, detecting, and taking action against trademark infringement with template letters and legal frameworks.',
      type: 'Whitepaper',
      pages: 38,
      format: 'PDF',
      size: '3.5 MB',
      featured: false,
      image: 'https://images.unsplash.com/photo-1619418602850-35ad20aa1700?w=800&h=600&fit=crop'
    },
    {
      id: 7,
      slug: 'building-brand-value-through-ip',
      title: 'Building Brand Value Through IP Protection',
      description: 'Learn how trademark protection increases company valuation, attracts investors, and creates long-term business value.',
      type: 'Whitepaper',
      pages: 34,
      format: 'PDF',
      size: '3.2 MB',
      featured: false,
      image: 'https://images.unsplash.com/photo-1730382624709-81e52dd294d4?w=800&h=600&fit=crop'
    },
    {
      id: 8,
      slug: 'social-media-trademark-management',
      title: 'Social Media Trademark Management',
      description: 'Master the art of protecting your brand across social platforms with strategies for handles, profiles, and content protection.',
      type: 'Guide',
      pages: 36,
      format: 'PDF',
      size: '3.4 MB',
      featured: false,
      image: 'https://images.unsplash.com/photo-1683721003111-070bcc053d8b?w=800&h=600&fit=crop'
    },
    {
      id: 9,
      slug: 'trademark-maintenance-renewal-guide',
      title: 'Trademark Maintenance & Renewal Guide',
      description: 'Never miss a deadline with this comprehensive guide to maintaining your trademark portfolio and filing renewal documents.',
      type: 'Guide',
      pages: 28,
      format: 'PDF',
      size: '2.8 MB',
      featured: false,
      image: 'https://images.unsplash.com/photo-1531403009284-440f080d1e12?w=800&h=600&fit=crop'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Free Trademark Guides & Publications | Norfolk Trademark"
        description="Download free guides on trademark registration, brand protection, and IP strategy from legal experts."
        keywords="trademark guides, IP publications, brand protection guides, trademark resources"
        canonical="/resources/publications"
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#FF035B] via-[#2F1A8E] to-[#FFA06B] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <BookOpen className="w-4 h-4" />
              Free Resources
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Publications & Resources
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Download our comprehensive guides, whitepapers, and eBooks to master trademark protection
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Featured Publications */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-1 w-12 bg-gradient-to-r from-[#FF035B] to-[#2F1A8E] rounded-full"></div>
            <h2 className="text-3xl font-bold text-gray-900">Featured Publications</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {publications.filter(item => item.featured).map((pub) => (
              <article
                key={pub.id}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group"
              >
                <div className="grid md:grid-cols-2 gap-6 p-8">
                  <div className="relative h-64 rounded-xl overflow-hidden">
                    <img
                      src={pub.image}
                      alt={pub.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="bg-[#FF035B] text-white px-3 py-1 rounded-lg text-sm font-semibold">
                        {pub.type}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col justify-between">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-[#FF035B] transition-colors">
                        {pub.title}
                      </h3>
                      <p className="text-gray-600 mb-4">
                        {pub.description}
                      </p>
                    </div>

                    <div>
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                        <div className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          <span>{pub.pages} pages</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Download className="w-4 h-4" />
                          <span>{pub.size}</span>
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <Link to={`/resources/publications/${pub.slug}`} className="flex-1 bg-gradient-to-r from-[#FF035B] to-[#2F1A8E] hover:from-[#2F1A8E] hover:to-[#FF035B] text-white font-semibold px-4 py-2 rounded-lg transition-all duration-300 flex items-center justify-center gap-2">
                          <Download className="w-4 h-4" />
                          Download
                        </Link>
                        <Link to={`/resources/publications/${pub.slug}`} className="border-2 border-gray-200 hover:border-[#FF035B] text-gray-700 hover:text-[#FF035B] font-semibold px-4 py-2 rounded-lg transition-all duration-300 flex items-center justify-center gap-2">
                          <Eye className="w-4 h-4" />
                          Preview
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* All Publications */}
        <div>
          <div className="flex items-center gap-3 mb-8">
            <div className="h-1 w-12 bg-gradient-to-r from-[#FF035B] to-[#2F1A8E] rounded-full"></div>
            <h2 className="text-3xl font-bold text-gray-900">All Publications</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {publications.filter(item => !item.featured).map((pub) => (
              <article
                key={pub.id}
                className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={pub.image}
                    alt={pub.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-3 right-3">
                    <span className="bg-black/70 text-white px-2 py-1 rounded text-xs font-semibold">
                      {pub.type}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-[#FF035B] transition-colors line-clamp-2">
                    {pub.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {pub.description}
                  </p>

                  <div className="flex items-center gap-4 text-xs text-gray-500 mb-4 pb-4 border-b border-gray-100">
                    <div className="flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      <span>{pub.pages}p</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="font-medium">{pub.format}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span>{pub.size}</span>
                    </div>
                  </div>

                  <Link to={`/resources/publications/${pub.slug}`} className="w-full bg-gradient-to-r from-[#FF035B] to-[#2F1A8E] hover:from-[#2F1A8E] hover:to-[#FF035B] text-white font-semibold px-4 py-2 rounded-lg transition-all duration-300 flex items-center justify-center gap-2">
                    <Download className="w-4 h-4" />
                    Download Free
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Need Personalized Guidance?
          </h2>
          <p className="text-gray-300 mb-8">
            Talk to our trademark experts for customized advice on protecting your brand
          </p>
          <Link
            to="/contact"
            className="inline-block bg-[#FF035B] hover:bg-[#2F1A8E] text-white font-bold px-8 py-3 rounded-lg transition-all duration-300 transform hover:scale-105"
          >
            Schedule Free Consultation
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PublicationsPage;
